import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

export default function Impressum() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/site-info").then((r) => setInfo(r.data)).catch(() => setInfo({}));
    document.title = "Impressum · Malermeister Santo Russo";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Impressum und rechtliche Informationen zu Malermeister Santo Russo, Mainaschaff.");
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, follow");
  }, []);

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center bg-sand-100 font-display text-2xl text-stone-900">Laden…</div>;
  }

  return (
    <div data-testid="impressum-page" className="min-h-screen bg-sand-100 text-stone-900">
      <header className="border-b border-sand-300 bg-sand-100/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="impressum-back-link" className="inline-flex items-center gap-2 text-stone-700 hover:text-clay transition-colors">
            <ChevronLeft className="w-4 h-4" /> Zurück zur Webseite
          </Link>
          <span className="font-display text-xl text-stone-900 hidden sm:block">{info.company_name}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
        <p className="text-xs uppercase tracking-[0.25em] text-sage font-bold mb-4">Rechtliche Hinweise</p>
        <h1 className="font-display text-5xl sm:text-6xl text-stone-900 mb-12 leading-tight">Impressum</h1>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Angaben gemäß § 5 TMG</h2>
          <address className="not-italic text-stone-700 leading-relaxed">
            <strong className="text-stone-900">{info.company_name}</strong><br />
            Inhaber: Santo Russo<br />
            {info.address?.split(",").map((line, i, arr) => (
              <span key={i}>{line.trim()}{i < arr.length - 1 ? <br /> : ""}</span>
            ))}
          </address>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Kontakt</h2>
          <ul className="space-y-2 text-stone-700">
            <li><span className="text-stone-600 inline-block w-20">Telefon:</span> <a href={`tel:${info.phone?.replace(/\s+/g, "")}`} className="hover:text-clay transition-colors">{info.phone}</a></li>
            {info.email && (
              <li><span className="text-stone-600 inline-block w-20">E-Mail:</span> <a href={`mailto:${info.email}`} className="hover:text-clay transition-colors">{info.email}</a></li>
            )}
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Berufsbezeichnung und berufsrechtliche Regelungen</h2>
          <p className="text-stone-700 leading-relaxed mb-3">
            <strong className="text-stone-900">Berufsbezeichnung:</strong> Malermeister (verliehen in Deutschland)
          </p>
          <p className="text-stone-700 leading-relaxed">
            <strong className="text-stone-900">Zuständige Kammer:</strong> Handwerkskammer Aschaffenburg<br />
            Es gelten die Bestimmungen der Handwerksordnung (HwO).
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Umsatzsteuer-ID</h2>
          <p className="text-stone-700 leading-relaxed">
            Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz: <em className="text-stone-600">Wird auf Anfrage mitgeteilt.</em>
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
          <p className="text-stone-700 leading-relaxed">
            Santo Russo<br />
            {info.address}
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Streitschlichtung</h2>
          <p className="text-stone-700 leading-relaxed mb-3">
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
            <br />
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-clay hover:underline">https://ec.europa.eu/consumers/odr</a>
          </p>
          <p className="text-stone-700 leading-relaxed">
            Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Haftung für Inhalte</h2>
          <p className="text-stone-700 leading-relaxed">
            Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.
            Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen
            oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Haftung für Links</h2>
          <p className="text-stone-700 leading-relaxed">
            Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte
            auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl text-stone-900 mb-4">Urheberrecht</h2>
          <p className="text-stone-700 leading-relaxed">
            Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
            Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen
            Autors bzw. Erstellers.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-stone-900 mb-4">Webdesign</h2>
          <p className="text-stone-700 leading-relaxed">
            Konzept &amp; Umsetzung dieser Webseite:&nbsp;
            <a href="https://www.kauzwerk.de" target="_blank" rel="noopener noreferrer" className="text-clay hover:underline">KauzWerk · www.kauzwerk.de</a>
          </p>
        </section>
      </main>

      <footer className="border-t border-sand-300 py-8">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600">
          <p>© {new Date().getFullYear()} {info.company_name}. Alle Rechte vorbehalten.</p>
          <div className="flex gap-6">
            <Link to="/datenschutz" className="hover:text-clay transition-colors">Datenschutz</Link>
            <Link to="/" className="hover:text-clay transition-colors">Zurück zur Startseite</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
